import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  LifeBuoy,
  Mail,
  Send,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/Navbar";
import RecruitGlanceLogo from "../components/RecruitGlanceLogo";
import { apiFetch } from "../utils/api";

const HERO_TITLE = "Turn CV emails into an evidence-backed shortlist.";
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "recruitglance@gmail.com";

export default function Home() {
  const navigate = useNavigate();
  const [typedHeroTitle, setTypedHeroTitle] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    verification_confirmed: false,
    website_url: "",
  });
  const [contactStatus, setContactStatus] = useState({
    type: "",
    message: "",
  });
  const [contactSending, setContactSending] = useState(false);

  const fadeUp = {
    hidden: { opacity: 0, y: 36 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  useEffect(() => {
    setTypedHeroTitle("");

    const typeSpeed = 42;
    let index = 0;

    const intervalId = setInterval(() => {
      index += 1;
      setTypedHeroTitle(HERO_TITLE.slice(0, index));

      if (index >= HERO_TITLE.length) {
        clearInterval(intervalId);
      }
    }, typeSpeed);

    return () => clearInterval(intervalId);
  }, []);

  const updateContactForm = (field, value) => {
    setContactForm((current) => ({ ...current, [field]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    setContactSending(true);
    setContactStatus({ type: "", message: "" });

    try {
      const response = await apiFetch("/contact", {
        method: "POST",
        body: JSON.stringify(contactForm),
      });

      if (!response.ok) {
        throw new Error("Could not send the message.");
      }

      setContactForm({
        name: "",
        email: "",
        company: "",
        message: "",
        verification_confirmed: false,
        website_url: "",
      });
      setContactStatus({
        type: "success",
        message: "We have received your question. We will get back to you as soon as possible.",
      });
    } catch (error) {
      setContactStatus({
        type: "error",
        message: "Could not send the message. Please email recruitglance@gmail.com directly.",
      });
    } finally {
      setContactSending(false);
    }
  };

  const screenshotSlides = [
    {
      title: "Recruiter Dashboard",
      desc: "Track scan volume, hiring signals, shortlisted candidates, and job quality from one workspace.",
      image: "/media/product-tour-dashboard.png",
    },
    {
      title: "Candidate Intake",
      desc: "Import job-linked applicants from direct upload or Outlook, with Gmail support marked as in progress during verification.",
      image: "/media/product-tour-candidates.png",
    },
    {
      title: "Bulk Scan",
      desc: "Select a job, review related applicants, add general candidates, and run a focused scan.",
      image: "/media/product-tour-bulk-scan.png",
    },
    {
      title: "Rankings",
      desc: "Review all scan batches for a job with scores, shortlist actions, notes, and comparison.",
      image: "/media/product-tour-rankings.png",
    },
  ];

  const workflow = [
    ["Select the job", "Keep every inbox import and scan tied to a real hiring role."],
    ["Import from inbox", "Bring in recent Outlook CV emails, with Gmail support in progress, without opening every attachment manually."],
    ["Approve candidates", "Review staged CVs before they are saved into the recruiter workspace."],
    ["Rank and decide", "See evidence-backed rankings, gaps, notes, shortlist actions, and exports."],
  ];

  const easyStartHighlights = [
    "No ATS migration",
    "No hiring system rebuild",
    "Start from the inbox overload",
  ];

  const decisionSupportHighlights = [
    {
      label: "Automatic decisions",
      status: "avoid",
    },
    {
      label: "Black-box rejection",
      status: "avoid",
    },
    {
      label: "Decisions stay human.",
      status: "support",
    },
  ];

  const scoringLayers = [
    {
      title: "Semantic fit",
      desc: "Understands the meaning of the job description and CV, even when the wording is different.",
      icon: Search,
    },
    {
      title: "Skill evidence",
      desc: "Checks must-have and preferred skills against actual CV signals, not just a simple keyword count.",
      icon: CheckCircle2,
    },
    {
      title: "Experience evidence",
      desc: "Looks for required skills inside work experience or project context, so listed keywords alone do not over-inflate the score.",
      icon: Briefcase,
    },
    {
      title: "AI reasoning",
      desc: "Explains why top candidates rank well, where the gaps are, and what to ask in interviews.",
      icon: Sparkles,
    },
  ];

  const proofPoints = [
    { value: "Inbox CVs", label: "Applicants arrive from Outlook, direct upload, or Gmail when available", icon: Mail },
    { value: "Job Filter", label: "Recent emails are checked against the selected job before import", icon: Search },
    { value: "Recruiter Approval", label: "Matching CVs are staged for review before they are saved", icon: CheckCircle2 },
    { value: "Evidence Ranking", label: "Candidates are ranked with score breakdowns and match evidence", icon: Sparkles },
    { value: "Recruiter Decision", label: "Shortlist, reject, compare, export, or contact candidates", icon: Briefcase },
  ];

  const capabilities = [
    "Outlook CV import for selected jobs, with Gmail in progress",
    "Recruiter approval before imported CVs are saved",
    "Evidence-backed candidate ranking",
    "Semantic fit, skill evidence, experience evidence, and AI reasoning",
    "Shortlisting, rejection, comparison, and recruiter notes",
    "Candidate Re-discovery from the saved applicant pool",
    "Works alongside existing ATS and HR tools",
    "Enterprise API access with usage history",
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "€0",
      description: "Test the job-to-CV scoring workflow with a small recruiter-led trial.",
      highlight: false,
      features: [
        "5 single candidate-job scans",
        "Create jobs and test one CV at a time",
        "Baseline match score and detected signals",
        "Recruiter dashboard preview",
      ],
      unavailable: ["Bulk Scan", "Candidate import", "AI Talent Search", "API access"],
      cta: "Create free account",
    },
    {
      name: "Basic",
      price: "€39",
      standardPrice: "€49",
      priceNote: "Early customer price",
      description: "For recruiters who want structured screening without advanced search or API access.",
      highlight: true,
      badge: "Best first paid step",
      features: [
        "200 candidate-job scans",
        "Bulk Scan and candidate import",
        "Reusable candidate pool by job",
        "Score breakdown, detected signals, and criteria evidence",
        "Shortlist, reject, notes, rankings, and scan history",
        "Candidate Re-discovery from saved applicants",
      ],
      unavailable: ["AI Talent Search", "Inbox to Shortlist automation", "Candidate decision email sending", "API access"],
      cta: "Start Basic",
    },
    {
      name: "Pro",
      price: "€119",
      standardPrice: "€149",
      priceNote: "Early customer price",
      description: "For active recruiters who need AI-assisted search, mailbox workflows, and more monthly capacity.",
      highlight: false,
      features: [
        "500 candidate-job scans",
        "Everything in Basic",
        "Top-candidate AI enrichment and interview questions",
        "AI Talent Search across the candidate pool",
        "Inbox to Shortlist and email import workflows",
        "Candidate decision email sending through connected Gmail/Outlook",
        "25 API trial calls",
      ],
      unavailable: ["Production API access", "Dedicated isolated deployment"],
      cta: "Start Pro",
    },
    {
      name: "Enterprise",
      price: "Custom",
      description: "For teams that need an isolated workspace, dedicated processing, production API access, and custom usage limits.",
      highlight: false,
      features: [
        "Custom scan volume",
        "Multi-user team workspace",
        "Dedicated RecruitGlance deployment",
        "Isolated database and candidate storage",
        "Dedicated scan and indexing queues",
        "Candidate decision email sending through connected Gmail/Outlook",
        "Production API access",
        "Custom API rate limits",
        "ATS/HR tool integrations",
        "Priority support",
        "Custom onboarding",
      ],
      unavailable: [],
      cta: "Contact us",
    },
  ];

  const demoCandidates = [
    {
      name: "A. Fischer",
      role: "CV attached from Gmail",
      score: 92,
      skills: ["PHP", "SQL", "Git"],
      color: "emerald",
    },
    {
      name: "M. Keller",
      role: "CV attached from Outlook",
      score: 81,
      skills: ["Linux", "Bash", "API"],
      color: "sky",
    },
    {
      name: "S. Novak",
      role: "CV attached from Gmail",
      score: 68,
      skills: ["HTML", "CSS", "JS"],
      color: "amber",
    },
  ];

  const demoSteps = [
    "Job selected",
    "Candidates loaded",
    "AI ranking",
    "Shortlist ready",
  ];

  return (
    <div className="overflow-x-hidden bg-white text-slate-950">
      <Navbar />

      {/* HERO */}
      <section className="border-b border-slate-200 bg-white px-6 pb-20 pt-36">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[minmax(0,1.02fr)_minmax(360px,0.98fr)]">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="text-center lg:text-left">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <Sparkles size={14} />
              AI recruitment workspace
            </div>

            <p className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-emerald-700">
              RecruitGlance
            </p>

            <h1 className="mx-auto max-w-5xl text-5xl font-semibold tracking-tight text-slate-950 md:text-7xl lg:mx-0">
              <span aria-label={HERO_TITLE}>
                {typedHeroTitle}
                <span className="ml-1 inline-block h-[0.85em] w-[3px] translate-y-1 animate-pulse rounded-full bg-emerald-700" />
              </span>
            </h1>

            <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl lg:mx-0">
              RecruitGlance helps recruiters import CVs from Gmail or Outlook, review job-matched applicants, rank candidates with evidence, and decide faster without replacing their ATS.
            </p>

            <div className="mx-auto mt-6 grid max-w-3xl gap-2 sm:grid-cols-3 lg:mx-0">
              {decisionSupportHighlights.map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-semibold shadow-sm ${
                    item.status === "avoid"
                      ? "border-rose-300 bg-white text-rose-950"
                      : "border-emerald-400 bg-emerald-100 text-emerald-950"
                  }`}
                >
                  {item.status === "avoid" ? (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-rose-400 bg-rose-100 text-xs font-black text-rose-800">
                        -
                      </span>
                  ) : (
                    <CheckCircle2 size={17} className="shrink-0 text-emerald-700" />
                  )}
                  <span className={item.status === "avoid" ? "font-bold line-through decoration-2 decoration-rose-700" : "font-bold"}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mx-auto mt-7 flex max-w-2xl flex-col items-center justify-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-5 py-4 shadow-sm sm:flex-row lg:mx-0 lg:justify-start">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
                <CheckCircle2 size={22} />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-base font-bold text-emerald-950">Try RecruitGlance free with 5 Single CV scans</p>
                <p className="mt-1 text-sm font-medium text-emerald-800">
                  No paid plan needed to test the core CV-to-job matching workflow.
                </p>
              </div>
            </div>

            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
              <button
                onClick={() => navigate("/register")}
                className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-6 text-sm font-semibold text-white transition hover:bg-emerald-900"
              >
                Try 5 free scans
                <ArrowRight size={16} />
              </button>

              <button
                onClick={() => document.getElementById("product-demo")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex min-h-[46px] items-center justify-center rounded-lg border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
              >
                View product tour
              </button>
            </div>
          </motion.div>

          <motion.div
            id="product-demo"
            initial={{ opacity: 0, x: 34 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mx-auto w-full max-w-[520px] overflow-hidden rounded-xl border border-slate-200 bg-slate-950 shadow-[0_24px_80px_rgba(15,23,42,0.18)]"
          >
            <div className="flex items-center justify-between border-b border-white/10 bg-slate-900 px-4 py-3 text-left">
              <div>
                <p className="text-sm font-semibold text-white">RecruitGlance Demo</p>
                <p className="text-xs font-semibold text-lime-200">Inbox-to-shortlist workflow with AI evidence</p>
              </div>
              <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                Live product flow
              </span>
            </div>

            <div className="bg-[#02150f] p-4 text-left">
              <div className="relative overflow-hidden rounded-xl border border-emerald-300/20 bg-[#031f17] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-200">Inbox refinery</p>
                    <h3 className="mt-1 text-lg font-semibold text-white">Backend Developer</h3>
                  </div>
                  <motion.span
                    animate={{ opacity: [0.55, 1, 0.55] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className="rounded-full border border-sky-300/40 bg-sky-300/10 px-3 py-1 text-xs font-bold text-sky-100"
                  >
                    12 emails found
                  </motion.span>
                </div>

                <div className="grid grid-cols-[1fr_86px_1fr] items-center gap-3">
                  <div className="space-y-2">
                    {["Gmail CV", "Outlook CV", "PDF resume"].map((label, index) => (
                      <motion.div
                        key={label}
                        animate={{ x: [0, 8, 0], opacity: [0.65, 1, 0.85] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.28 }}
                        className="flex items-center gap-2 rounded-lg border border-sky-300/20 bg-sky-300/10 px-3 py-2 text-xs font-semibold text-sky-50"
                      >
                        <Mail size={13} />
                        <span>{label}</span>
                      </motion.div>
                    ))}
                  </div>

                  <div className="relative mx-auto flex h-24 w-20 items-center justify-center">
                    <motion.div
                      aria-hidden="true"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 5.5, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 rounded-2xl border border-sky-300/30 border-t-sky-300"
                    />
                    <motion.div
                      aria-hidden="true"
                      animate={{ scale: [0.82, 1.06, 0.82], opacity: [0.45, 1, 0.45] }}
                      transition={{ duration: 1.8, repeat: Infinity }}
                      className="absolute inset-3 rounded-xl bg-emerald-300/10 shadow-[0_0_34px_rgba(56,189,248,0.38)]"
                    />
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-950/30">
                      <Search size={19} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    {demoCandidates.slice(0, 3).map((candidate, index) => (
                      <motion.div
                        key={candidate.name}
                        animate={{ x: [0, -6, 0], opacity: [0.72, 1, 0.9] }}
                        transition={{ duration: 2.2, repeat: Infinity, delay: index * 0.32 }}
                        className="rounded-lg border border-emerald-300/20 bg-white px-3 py-2 text-slate-950 shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="rounded-md bg-slate-950 px-2 py-1 text-[10px] font-black text-white">
                            #{index + 1}
                          </span>
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-800">
                            {candidate.score}%
                          </span>
                        </div>
                        <p className="mt-1 truncate text-xs font-bold">{candidate.name}</p>
                        <p className="text-[10px] font-semibold text-emerald-700">Shortlist CV</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.div
                  animate={{ opacity: [0.78, 1, 0.78], y: [0, -1, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  className="mt-3 rounded-lg border border-lime-300/40 bg-lime-300/15 px-3 py-2 shadow-[0_0_28px_rgba(190,242,100,0.14)]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-lime-300 text-slate-950">
                        <CheckCircle2 size={15} />
                      </span>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-lime-100">Recruiter approval</p>
                        <p className="text-[11px] font-semibold text-lime-50/80">CVs are staged before they enter ranking</p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full bg-lime-50 px-2.5 py-1 text-[11px] font-black text-lime-800">
                      7 staged
                    </span>
                  </div>
                </motion.div>

                <div className="mt-3 grid grid-cols-4 gap-2">
                  {["Emails in", "Staged", "AI checks", "Ranked"].map((step, index) => (
                    <motion.div
                      key={step}
                      animate={{ opacity: [0.55, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.28 }}
                      className="rounded-lg border border-white/10 bg-white/5 px-2 py-2 text-center text-[11px] font-bold text-emerald-50"
                    >
                      {step}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hidden">
              <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="grid grid-cols-[1fr_18px_1fr_18px_1fr] items-center gap-2 text-center">
                  {[
                    { label: "Email", icon: Mail, color: "bg-sky-100 text-sky-700" },
                    { label: "CV review", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
                    { label: "Shortlist", icon: Briefcase, color: "bg-slate-950 text-white" },
                  ].map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} className="contents">
                        <motion.div
                          animate={{ y: [0, -2, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.3 }}
                          className={`flex min-h-[38px] flex-col items-center justify-center rounded-md ${item.color}`}
                        >
                          <Icon size={14} />
                          <span className="mt-0.5 text-[10px] font-bold leading-none">{item.label}</span>
                        </motion.div>
                        {index < 2 && (
                          <motion.span
                            animate={{ opacity: [0.35, 1, 0.35], x: [0, 2, 0] }}
                            transition={{ duration: 1.4, repeat: Infinity, delay: index * 0.25 }}
                            className="text-sm font-black text-emerald-700"
                          >
                            →
                          </motion.span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <motion.div
                  animate={{ y: [0, 3, 0], opacity: [0.45, 1, 0.45] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                  className="mx-auto mt-1 text-center text-xs font-black text-emerald-700"
                >
                  ↓
                </motion.div>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                {["Inbox matched", "CVs ranked", "Shortlist ready"].map((step, index) => (
                  <motion.div
                    key={step}
                    animate={{ opacity: [0.55, 1, 0.7] }}
                    transition={{
                      duration: 2.4,
                      repeat: Infinity,
                      delay: index * 0.35,
                    }}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-center"
                  >
                    <span className="mx-auto flex h-6 w-6 items-center justify-center rounded-md bg-slate-950 text-xs font-bold text-white">
                      {index + 1}
                    </span>
                    <p className="mt-1 text-[11px] font-semibold text-slate-600">{step}</p>
                  </motion.div>
                ))}
              </div>

              <div className="relative mt-3 grid gap-2 overflow-hidden">
                <motion.div
                  aria-hidden="true"
                  animate={{ y: ["-120%", "260%"] }}
                  transition={{
                    duration: 2.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="pointer-events-none absolute left-0 right-0 z-10 h-9 rounded-lg border border-emerald-400/60 bg-emerald-300/10 shadow-[0_0_28px_rgba(16,185,129,0.28)]"
                />
                {demoCandidates.slice(0, 2).map((candidate, index) => (
                  <motion.div
                    key={candidate.name}
                    initial={{ opacity: 0, x: 18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.55,
                      delay: index * 0.2,
                      repeat: Infinity,
                      repeatDelay: 4,
                    }}
                    className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
                            <Mail size={12} />
                            Inbox
                          </span>
                          {index === 0 && (
                            <span className="rounded-md bg-amber-100 px-2 py-1 text-xs font-bold text-amber-700">
                              Ready to approve
                            </span>
                          )}
                        </div>
                        <p className="mt-2 truncate text-sm font-semibold text-slate-950">{candidate.name}</p>
                        <p className="text-xs text-slate-500">{candidate.role}</p>
                      </div>

                      <div className="w-32 shrink-0">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-slate-700">{candidate.score}%</span>
                          <span className="text-xs text-slate-500">job fit</span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                          <motion.div
                            className={`h-full rounded-full ${
                              candidate.color === "emerald"
                                ? "bg-emerald-500"
                                : candidate.color === "sky"
                                ? "bg-sky-500"
                                : "bg-amber-500"
                            }`}
                            initial={{ width: "20%" }}
                            animate={{ width: `${candidate.score}%` }}
                            transition={{
                              duration: 1,
                              delay: index * 0.2,
                              repeat: Infinity,
                              repeatDelay: 4,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EASY START */}
      <section className="border-b border-slate-200 bg-emerald-950 px-6 py-16 text-white">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-300">Built for easy adoption</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
              Start where recruiter overload usually begins: the inbox.
            </h2>
            <p className="mt-5 text-lg leading-8 text-emerald-50/85">
              Recruiters do not need another hiring system to rebuild. RecruitGlance helps turn messy CV emails into a reviewed shortlist before the ATS workflow even starts.
            </p>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/10 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
            <div className="grid gap-3">
              {easyStartHighlights.map((item, index) => (
                <motion.div
                  key={item}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-950"
                  initial={{ opacity: 0, x: 18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, delay: index * 0.12 }}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-sm font-bold text-emerald-800">
                    {index + 1}
                  </span>
                  <span className="text-sm font-semibold">{item}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-5 rounded-lg border border-emerald-300/30 bg-emerald-400/10 p-4">
              <p className="text-sm font-semibold text-emerald-100">
                Useful even if you already have an ATS.
              </p>
              <p className="mt-2 text-sm leading-6 text-emerald-50/80">
                Use RecruitGlance beside your existing ATS to clean up the inbox-to-shortlist step before candidates are copied, exported, or reviewed elsewhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RECRUITER WORKFLOW */}
      <section className="border-b border-slate-200 bg-slate-50 px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">From inbox to shortlist</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">
                Turn incoming CV emails into ranked hiring evidence.
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                RecruitGlance checks recent applicant emails, stages relevant CVs for recruiter approval, extracts useful candidate signals, and turns the batch into a shortlist recruiters can act on.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <motion.div
                className="absolute left-10 right-10 top-[72px] hidden h-1 rounded-full bg-emerald-100 md:block"
                initial={{ opacity: 0.4 }}
                animate={{ opacity: [0.35, 1, 0.35] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute top-[65px] hidden h-4 w-4 rounded-full bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.7)] md:block"
                initial={{ left: "8%" }}
                animate={{ left: ["8%", "28%", "48%", "68%", "88%"] }}
                transition={{ duration: 4.6, repeat: Infinity, ease: "easeInOut" }}
              />

              <div className="relative grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {proofPoints.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-80px" }}
                      animate={{
                        scale: [1, 1.025, 1],
                        borderColor: ["#e2e8f0", "#10b981", "#e2e8f0"],
                      }}
                      transition={{
                        duration: 1.7,
                        delay: index * 0.25,
                        repeat: Infinity,
                        repeatDelay: 3.8,
                        ease: "easeInOut",
                      }}
                      className="relative min-h-[180px] rounded-lg border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-900 text-white shadow-sm">
                          <Icon size={18} />
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-slate-500">
                          {index + 1}
                        </span>
                      </div>
                      <p className="text-lg font-semibold tracking-tight text-slate-950">{item.value}</p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{item.label}</p>

                      {index === 0 && (
                        <div className="mt-4 flex gap-2">
                          {[0, 1, 2].map((fileIndex) => (
                            <motion.div
                              key={fileIndex}
                              className="h-10 w-8 rounded border border-slate-300 bg-white shadow-sm"
                              animate={{ y: [0, -4, 0] }}
                              transition={{
                                duration: 1.4,
                                delay: fileIndex * 0.18,
                                repeat: Infinity,
                                repeatDelay: 2.8,
                              }}
                            >
                              <div className="mx-auto mt-2 h-1 w-4 rounded bg-slate-300" />
                              <div className="mx-auto mt-1 h-1 w-5 rounded bg-slate-200" />
                              <div className="mx-auto mt-1 h-1 w-3 rounded bg-slate-200" />
                            </motion.div>
                          ))}
                        </div>
                      )}

                      {index === 1 && (
                        <div className="mt-4 space-y-2">
                          {["Readable CV", "No duplicate", "Job selected"].map((label, checkIndex) => (
                            <motion.div
                              key={label}
                              className="flex items-center gap-2 text-xs font-semibold text-slate-600"
                              animate={{ opacity: [0.45, 1, 0.45] }}
                              transition={{
                                duration: 1.6,
                                delay: checkIndex * 0.25,
                                repeat: Infinity,
                                repeatDelay: 2.6,
                              }}
                            >
                              <CheckCircle2 size={14} className="text-emerald-600" />
                              {label}
                            </motion.div>
                          ))}
                        </div>
                      )}

                      {index === 2 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {["PHP", "SQL", "Git", "B.Sc"].map((signal, signalIndex) => (
                            <motion.span
                              key={signal}
                              className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-emerald-700 shadow-sm"
                              animate={{ scale: [1, 1.08, 1] }}
                              transition={{
                                duration: 1.5,
                                delay: signalIndex * 0.2,
                                repeat: Infinity,
                                repeatDelay: 2.8,
                              }}
                            >
                              {signal}
                            </motion.span>
                          ))}
                        </div>
                      )}

                      {index === 3 && (
                        <div className="mt-4 space-y-2">
                          {[92, 84, 76].map((score, scoreIndex) => (
                            <div key={score} className="flex items-center gap-2">
                              <span className="w-7 text-xs font-bold text-slate-500">#{scoreIndex + 1}</span>
                              <div className="h-2 flex-1 overflow-hidden rounded-full bg-white">
                                <motion.div
                                  className="h-full rounded-full bg-emerald-600"
                                  initial={{ width: "20%" }}
                                  animate={{ width: `${score}%` }}
                                  transition={{
                                    duration: 1.4,
                                    delay: scoreIndex * 0.2,
                                    repeat: Infinity,
                                    repeatDelay: 3.2,
                                  }}
                                />
                              </div>
                              <span className="text-xs font-bold text-emerald-700">{score}%</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {index === 4 && (
                        <div className="mt-4 space-y-2">
                          {["Shortlist", "Reject", "Email"].map((decision, decisionIndex) => (
                            <motion.div
                              key={decision}
                              className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm"
                              animate={{ x: [0, 3, 0] }}
                              transition={{
                                duration: 1.5,
                                delay: decisionIndex * 0.18,
                                repeat: Infinity,
                                repeatDelay: 2.8,
                              }}
                            >
                              <span>{decision}</span>
                              <CheckCircle2 size={14} className="text-emerald-600" />
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCORING ENGINE */}
      <section className="border-b border-slate-200 bg-white px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Four-layer scoring engine</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              Beyond keyword matching.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              RecruitGlance does not rank CVs by simple word matching. It compares meaning, checks skill evidence, verifies hands-on experience context, applies recruiter-defined criteria, and explains why candidates are ranked.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {scoringLayers.map((layer, index) => {
              const Icon = layer.icon;

              return (
                <motion.div
                  key={layer.title}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-5 shadow-sm"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, delay: index * 0.12 }}
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-900 text-white">
                      <Icon size={19} />
                    </div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-700">
                      Layer {index + 1}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950">{layer.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{layer.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-700 text-white">
                  <ShieldCheck size={19} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-950">
                    Rankings support recruiters. They do not auto-reject candidates.
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-emerald-900">
                    RecruitGlance prioritizes candidates for human review and shows the reasoning behind the score. Final shortlist, rejection, and communication decisions stay with the recruiter.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                What recruiters see
              </p>
              <div className="mt-4 space-y-3">
                {[
                  "Why this CV is ranked here",
                  "Matched skills and missing gaps",
                  "Experience evidence behind important skills",
                  "Score breakdown by evidence layer",
                  "Interview questions for important gaps",
                  "Shortlist/rejection email workflow",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 size={17} className="shrink-0 text-emerald-600" />
                    <span className="text-sm font-medium text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT TOUR */}
      <section id="product-tour" className="scroll-mt-24 px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Product tour</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              A recruiter workspace built around evidence.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              RecruitGlance is not just a score. It shows why a candidate matches, where the gaps are, and how the recruiter should move next.
            </p>
          </div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="mt-12 rounded-2xl border border-slate-300 bg-slate-100 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.14)]"
          >
            <div className="grid gap-4 lg:grid-cols-[1.35fr_0.65fr]">
              <motion.div
                className="group overflow-hidden rounded-xl border border-slate-300 bg-white shadow-[0_14px_38px_rgba(15,23,42,0.12)] ring-1 ring-slate-950/5"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.25 }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-950">{screenshotSlides[0].title}</p>
                    <p className="text-xs text-slate-500">{screenshotSlides[0].desc}</p>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">Workspace overview</span>
                </div>
                <img
                  src={screenshotSlides[0].image}
                  alt={screenshotSlides[0].title}
                  className="aspect-video w-full border-t border-slate-200 bg-white object-cover transition duration-500 group-hover:scale-[1.015]"
                />
              </motion.div>

              <div className="grid gap-4">
                {screenshotSlides.slice(1, 3).map((slide, index) => (
                  <motion.div
                    key={slide.title}
                    className="group overflow-hidden rounded-xl border border-slate-300 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.1)] ring-1 ring-slate-950/5"
                    initial={{ opacity: 0, x: 22 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    whileHover={{ y: -4 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.12 }}
                  >
                    <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
                      <p className="text-xs font-bold text-slate-700">{slide.title}</p>
                      <span className="text-[11px] font-bold text-emerald-700">{String(index + 2).padStart(2, "0")}</span>
                    </div>
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="aspect-video w-full border-t border-slate-200 bg-white object-cover transition duration-500 group-hover:scale-[1.02]"
                    />
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="group overflow-hidden rounded-xl border border-slate-300 bg-white shadow-[0_14px_38px_rgba(15,23,42,0.12)] ring-1 ring-slate-950/5 lg:col-span-2"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
              >
                <div className="grid gap-0 lg:grid-cols-[0.32fr_0.68fr]">
                  <div className="border-b border-slate-200 p-5 lg:border-b-0 lg:border-r">
                    <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Decision view</p>
                    <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                      {screenshotSlides[3].title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{screenshotSlides[3].desc}</p>
                  </div>
                  <img
                    src={screenshotSlides[3].image}
                    alt={screenshotSlides[3].title}
                    className="aspect-video w-full border-t border-slate-200 bg-white object-cover transition duration-500 group-hover:scale-[1.01] lg:border-l lg:border-t-0"
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="scroll-mt-24 border-y border-slate-200 bg-slate-950 px-6 py-24 text-white">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-400">Recruiter flow</p>
              <h2 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">
                From CV emails to shortlist without inbox chaos.
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">
                The workflow keeps job context, inbox intake, CV evidence, AI reasoning, and recruiter decisions in one place.
              </p>
            </div>

            <div className="grid gap-3">
              {workflow.map(([title, desc], index) => (
                <div key={title} className="grid grid-cols-[56px_minmax(0,1fr)] gap-4 rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white text-sm font-bold text-slate-950">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{title}</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-300">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE AFTER */}
      <section className="px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Before</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                CVs scattered across email threads and attachments.
              </h2>
              <p className="mt-4 text-slate-600">
                Recruiters open messages, download PDFs, compare resumes by hand, and lose time before the actual shortlist work starts.
              </p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">After</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                Job-matched candidates ready for evidence review.
              </h2>
              <p className="mt-4 text-slate-600">
                RecruitGlance imports relevant CVs, asks the recruiter to approve them, ranks candidates by job fit, and keeps decisions human.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-y border-slate-200 bg-slate-50 px-6 py-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Platform coverage</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              A focused layer for inbox intake, screening, and shortlist decisions.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Every feature supports the same recruiter goal: move from incoming CVs to an explainable shortlist with less workflow friction.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {capabilities.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                <p className="text-sm font-medium leading-6 text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API */}
      <section className="px-6 py-24">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">API access</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              Add CV intelligence to your own systems.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Generate API keys, submit CVs programmatically, review usage history, and control cost with rate limits.
            </p>
          </div>

          <pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-950 p-6 text-sm leading-7 text-emerald-300">
{`POST /api/v1/cv/analyze
Authorization: Bearer YOUR_API_KEY
cv_file=@resume.pdf
job_name="Backend Engineer"
job_description="Python, APIs, PostgreSQL"`}
          </pre>
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="scroll-mt-24 border-y border-slate-200 bg-white px-6 py-20">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
          {[
            ["Private recruiter workspace", "Authenticated access keeps scan history, notes, and candidate records user-owned."],
            ["Duplicate-aware scanning", "Content hashes prevent repeated work and make rescan behavior explicit."],
            ["Cost controls", "Bulk limits, file validation, API history, and API rate limits protect usage."],
          ].map(([title, desc]) => (
            <div key={title} className="rounded-lg border border-slate-200 p-5">
              <ShieldCheck size={20} className="text-slate-700" />
              <h3 className="mt-4 font-semibold text-slate-950">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="scroll-mt-24 border-b border-slate-200 bg-slate-50 px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Pricing</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              Plans that match real recruiter usage.
            </h2>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-4">
            {pricingPlans.map((plan) => (
              <article
                key={plan.name}
                className={`relative flex flex-col rounded-lg border p-6 shadow-sm ${
                  plan.highlight
                    ? "border-slate-950 bg-white"
                    : "border-slate-200 bg-white"
                }`}
              >
                {plan.badge && (
                  <span className="absolute right-5 top-5 rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold text-white">
                    {plan.badge}
                  </span>
                )}

                <h3 className="text-xl font-semibold text-slate-950">{plan.name}</h3>
                {plan.standardPrice ? (
                  <div className="mt-4">
                    <div className="flex flex-wrap items-end gap-3">
                      <span className="pb-1 text-2xl font-semibold tracking-tight text-slate-400 line-through">
                        {plan.standardPrice}
                      </span>
                      <span className="text-4xl font-black tracking-tight text-emerald-900">{plan.price}</span>
                      <span className="pb-1 text-sm font-medium text-slate-500">/month</span>
                    </div>
                    <div className="mt-3 inline-flex items-center rounded-full border border-emerald-700 bg-emerald-950 px-3 py-1 text-xs font-black uppercase tracking-wide text-emerald-50 shadow-[0_0_20px_rgba(6,78,59,0.22)]">
                      {plan.priceNote}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex items-end gap-1">
                    <span className="text-4xl font-semibold tracking-tight text-slate-950">{plan.price}</span>
                    {plan.price !== "Custom" && (
                      <span className="pb-1 text-sm font-medium text-slate-500">/month</span>
                    )}
                  </div>
                )}
                <p className="mt-4 min-h-[52px] text-sm leading-6 text-slate-600">{plan.description}</p>

                <div className="mt-6 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <CheckCircle2 size={17} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                      <span className="text-sm leading-6 text-slate-700">{feature}</span>
                    </div>
                  ))}

                  {plan.unavailable.map((feature) => (
                    <div key={feature} className="flex items-start gap-3 opacity-50">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span className="text-sm leading-6 text-slate-600 line-through">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    if (plan.name === "Enterprise") {
                      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Enterprise%20plan%20request`;
                      return;
                    }
                    navigate("/register");
                  }}
                  className={`mt-8 inline-flex min-h-[44px] items-center justify-center rounded-lg px-5 text-sm font-semibold transition ${
                    plan.highlight
                      ? "bg-slate-950 text-white hover:bg-slate-800"
                      : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  {plan.cta}
                </button>
              </article>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-4xl rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-3 text-center text-sm font-semibold leading-6 text-emerald-950 shadow-sm">
            Early customer pricing is available for the first 20 paying customers and remains active while the subscription stays active. Standard prices are Basic €49/month and Pro €149/month.
          </p>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-24 border-b border-slate-200 bg-white px-6 py-24">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
          <div className="rounded-lg bg-[#03140f] p-8 text-white shadow-sm">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/25 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
              <LifeBuoy size={15} />
              Contact RecruitGlance
            </div>
            <h2 className="mt-6 text-4xl font-semibold tracking-tight md:text-5xl">
              Want to test RecruitGlance with real recruiter workflows?
            </h2>
            <p className="mt-5 text-lg leading-8 text-emerald-50/75">
              Ask about pricing, onboarding, isolated enterprise workspaces, or how the screening workflow fits your hiring process.
            </p>

            <div className="mt-8 grid gap-3 text-sm text-emerald-50/80">
              {[
                "Paid workspace setup for real CV screening",
                "Plan guidance for Basic, Pro, or Enterprise",
                "Data privacy and candidate handling questions",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3">
                  <CheckCircle2 size={17} className="text-emerald-300" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleContactSubmit} className="rounded-lg border border-slate-200 bg-slate-50 p-6 shadow-sm md:p-8">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-800">
                <Mail size={21} />
              </span>
              <div>
                <h3 className="text-2xl font-semibold text-slate-950">Send a message</h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Send your enquiry directly to RecruitGlance support.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                Name
                <input
                  required
                  value={contactForm.name}
                  onChange={(event) => updateContactForm("name", event.target.value)}
                  className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  placeholder="Your name"
                />
              </label>
              <label className="grid gap-2 text-sm font-semibold text-slate-700">
                Work email
                <input
                  required
                  type="email"
                  value={contactForm.email}
                  onChange={(event) => updateContactForm("email", event.target.value)}
                  className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  placeholder="name@company.com"
                />
              </label>
            </div>

            <label className="mt-4 grid gap-2 text-sm font-semibold text-slate-700">
              Company
              <input
                value={contactForm.company}
                onChange={(event) => updateContactForm("company", event.target.value)}
                className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                placeholder="Company or recruitment team"
              />
            </label>

            <label className="mt-4 grid gap-2 text-sm font-semibold text-slate-700">
              Message
              <textarea
                required
                value={contactForm.message}
                onChange={(event) => updateContactForm("message", event.target.value)}
                rows={5}
                className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm font-medium leading-6 text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                placeholder="Tell me what you want to screen, import, or validate."
              />
            </label>

            <label className="hidden">
              Website
              <input
                tabIndex={-1}
                autoComplete="off"
                value={contactForm.website_url}
                onChange={(event) => updateContactForm("website_url", event.target.value)}
              />
            </label>

            <label className="mt-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-950">
              <input
                type="checkbox"
                checked={contactForm.verification_confirmed}
                onChange={(event) => updateContactForm("verification_confirmed", event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-700 focus:ring-emerald-500"
              />
              <span>
                I confirm this is a genuine enquiry and I want RecruitGlance to contact me.
              </span>
            </label>

            <button
              type="submit"
              disabled={contactSending || !contactForm.verification_confirmed}
              className="mt-5 inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-800 px-6 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
            >
              <Send size={16} />
              {contactSending ? "Sending..." : "Contact RecruitGlance"}
            </button>
            {contactStatus.message && (
              <p
                className={`mt-4 rounded-lg border px-4 py-3 text-sm font-semibold ${
                  contactStatus.type === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-rose-200 bg-rose-50 text-rose-900"
                }`}
              >
                {contactStatus.message}
              </p>
            )}
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 text-center">
        <h2 className="mx-auto max-w-3xl text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
          Start screening candidates with clearer evidence.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-600">
          Create your recruiter workspace, add a job, and turn your next CV email batch into a ranked shortlist.
        </p>
        <button
          onClick={() => navigate("/register")}
          className="mt-8 inline-flex min-h-[46px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-6 text-sm font-semibold text-white transition hover:bg-emerald-900"
        >
          Get started
          <ArrowRight size={16} />
        </button>
      </section>

      <footer className="w-full bg-[#03140f] px-6 py-12 text-white">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 border-b border-white/10 pb-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
            <div className="max-w-xl">
              <div className="flex items-center gap-3">
                <RecruitGlanceLogo dark />
              </div>
              <p className="mt-5 text-sm leading-6 text-emerald-50/75">
                AI-assisted recruitment software for job-based CV screening,
                inbox candidate importing, semantic talent search, shortlisting, and
                recruiter decision support.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/register")}
                  className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-white px-5 text-sm font-semibold text-emerald-950 transition hover:bg-emerald-50"
                >
                  Try 5 free scans
                </button>
                <Link
                  to="/help"
                  className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-white/15 px-5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  View guide
                </Link>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-200/70">
                Product
              </p>
              <div className="mt-4 flex flex-col gap-3 text-sm text-emerald-50/75">
                <Link to="/login" className="transition hover:text-white">
                  Sign in
                </Link>
                <Link to="/register" className="transition hover:text-white">
                  Create account
                </Link>
                <Link to="/help" className="transition hover:text-white">
                  Help Center
                </Link>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-200/70">
                Platform
              </p>
              <div className="mt-4 flex flex-col gap-3 text-sm text-emerald-50/75">
                <span>Bulk CV Scan</span>
                <span>Candidate Library</span>
                <span>AI Talent Search</span>
                <span>API Access</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-200/70">
                Trust
              </p>
              <div className="mt-4 flex flex-col gap-3 text-sm text-emerald-50/75">
                <Link to="/privacy" className="transition hover:text-white">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="transition hover:text-white">
                  Terms of Use
                </Link>
                <Link to="/ai-disclaimer" className="transition hover:text-white">
                  AI Disclaimer
                </Link>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 pt-6 text-sm text-emerald-200/70 md:flex-row md:items-center md:justify-between">
            <p>&copy; {new Date().getFullYear()} RecruitGlance. All rights reserved.</p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={16} />
                Recruiter-controlled candidate data
              </span>
              <span>AI output requires human review</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
